import { z } from 'zod';
// Privacy policy version the UI must echo back on any account-creating
// request. Legal gating (Australian Privacy Act APP 1/5, AHPRA record-keeping)
// — there is no legitimate null case because the UI blocks submit until the
// user ticks the consent box.
const acceptedPrivacyPolicyVersionField = z
    .string()
    .min(1, 'Privacy policy consent is required')
    .max(20, 'Invalid privacy policy version');
// AHPRA registration number — Health Practitioner Regulation National Law.
// Format: 3-letter profession-code prefix + 10 digits. We capture and display
// the number but do NOT verify identity against the AHPRA public register.
//
// Profession codes are a CLOSED SET of 15 prefixes per AHPRA (see
// https://www.ahpra.gov.au/Registration/Registers-of-practitioners.aspx).
// The previous regex accepted any 3 uppercase letters (e.g. "FOO0000000000"
// was deemed valid), which let typos and obviously-junk values through.
const AHPRA_PROFESSION_CODES = [
    'ATSI', // Aboriginal and Torres Strait Islander Health Practice
    'CHI', // Chiropractic
    'CMR', // Chinese Medicine (registered)
    'DEN', // Dental
    'MED', // Medical
    'MRP', // Medical Radiation Practice
    'NMW', // Nursing and Midwifery
    'OCC', // Occupational Therapy
    'OPT', // Optometry
    'OST', // Osteopathy
    'PAR', // Paramedicine
    'PHA', // Pharmacy
    'PHY', // Physiotherapy
    'POD', // Podiatry
    'PSY', // Psychology
];
// 3- or 4-letter profession code anchored to the allowlist + 10 digits.
const ahpraNumberRegex = new RegExp(`^(${AHPRA_PROFESSION_CODES.join('|')})\\d{10}$`);
const ahpraNumberField = z
    .string()
    .regex(ahpraNumberRegex, 'AHPRA number must start with a recognised profession code (e.g. MED, NMW, PHA, DEN, PSY, PHY) followed by 10 digits');
export const loginSchema = z.object({
    email: z.string().email('Invalid email address'),
    // Match registerSchema's floor so payloads below the minimum never reach
    // the bcrypt comparison path. Register has always enforced 8+, so no
    // legacy accounts exist below this threshold.
    password: z
        .string()
        .min(8, 'Password must be at least 8 characters')
        .max(128, 'Password must be at most 128 characters'),
});
// Healthcare-grade password policy: min 8 chars, at least one uppercase,
// one lowercase, one digit, and one special character.
export const passwordField = z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(128, 'Password must be at most 128 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character');
export const registerSchema = z
    .object({
    email: z.string().email('Invalid email address'),
    password: passwordField,
    displayName: z
        .string()
        .min(2, 'Display name must be at least 2 characters')
        .max(64, 'Display name must be at most 64 characters')
        .trim(),
    // What the account holder wants their organisation called (e.g. "Park Holme
    // Chiropractic", "Nick's Practice"). Optional so clients that do not send it
    // — mobile and the extension both post to this endpoint — keep working; the
    // API falls back to a display-name-derived default.
    // `.trim()` leads here so the length bounds apply to the trimmed value.
    orgName: z
        .string()
        .trim()
        .min(2, 'Organisation name must be at least 2 characters')
        .max(64, 'Organisation name must be at most 64 characters')
        .optional(),
    acceptedPrivacyPolicyVersion: acceptedPrivacyPolicyVersionField,
    tosAcceptedAt: z.string().datetime().optional(),
    isPractitioner: z.boolean().default(false),
    ahpraNumber: ahpraNumberField.optional(),
    consentDetails: z
        .object({
        purpose: z.string(),
        timestamp: z.string(),
        ipAddress: z.string().optional(),
        userAgent: z.string(),
    })
        .optional(),
})
    .superRefine((data, ctx) => {
    if (data.isPractitioner && !data.ahpraNumber) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['ahpraNumber'],
            message: 'AHPRA number is required for practitioner accounts',
        });
    }
});
const inviteCodeField = z
    .string()
    .trim()
    .length(8, 'Invite code must be 8 characters')
    .regex(/^[A-Za-z0-9]+$/, 'Invite code must be alphanumeric')
    .transform((c) => c.toUpperCase());
export const joinWithInviteSchema = z
    .object({
    code: inviteCodeField,
    email: z.string().email('Invalid email address'),
    password: passwordField,
    displayName: z
        .string()
        .min(2, 'Display name must be at least 2 characters')
        .max(64, 'Display name must be at most 64 characters')
        .trim(),
    acceptedPrivacyPolicyVersion: acceptedPrivacyPolicyVersionField,
    tosAcceptedAt: z.string().datetime().optional(),
    isPractitioner: z.boolean().default(false),
    ahpraNumber: ahpraNumberField.optional(),
})
    .superRefine((data, ctx) => {
    if (data.isPractitioner && !data.ahpraNumber) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['ahpraNumber'],
            message: 'AHPRA number is required for practitioner accounts',
        });
    }
});
export const joinExistingSchema = z.object({
    code: inviteCodeField,
    acceptedPrivacyPolicyVersion: acceptedPrivacyPolicyVersionField,
});
/**
 * PATCH /users/me practitioner-status payload. Both fields are optional so
 * the caller can flip the flag independently of supplying/clearing a number.
 * The service layer enforces the "practitioner ⇒ number present" invariant
 * against the merged (existing + incoming) view of the row.
 */
export const updatePractitionerProfileSchema = z.object({
    isPractitioner: z.boolean().optional(),
    ahpraNumber: ahpraNumberField.optional().nullable(),
});
/**
 * Forgot-password request — only the email is needed. The endpoint always
 * returns 200 regardless of whether the address is registered, so this
 * schema is the only place client/server agree on the input shape.
 */
export const forgotPasswordSchema = z.object({
    email: z.string().email('Invalid email address').max(254, 'Email is too long'),
});
/**
 * Reset-password submission. The new password must satisfy the same
 * healthcare-grade strength rule used at registration; reusing the field
 * keeps validation in one place so a future tightening lands everywhere.
 */
export const resetPasswordSchema = z.object({
    token: z.string().min(20, 'Invalid reset token').max(256, 'Invalid reset token'),
    newPassword: passwordField,
});
// Single source of truth for the TOTP / backup-code shapes. Exported so
// packages/api/src/routes/mfa.routes.ts imports these instead of
// redeclaring the same two regex literals — the API keeps its own inline
// 400 response (different message text than this schema's, see the route
// for why), but the format check itself now lives in exactly one place.
export const TOTP_TOKEN_RE = /^\d{6}$/;
export const BACKUP_CODE_RE = /^[a-zA-Z0-9]{8,12}$/;
/**
 * POST /mfa/challenge submission — mirrors the API's TOTP_TOKEN_RE/
 * BACKUP_CODE_RE validation in packages/api/src/routes/mfa.routes.ts, so
 * clients can reject an obviously-malformed code before making the network
 * call. The server remains the source of truth; this is client-side
 * pre-validation only, same role loginSchema plays for the credentials step.
 */
export const mfaChallengeSchema = z
    .object({
    // Backup codes are frequently pasted from a saved list; TOTP codes are
    // sometimes autofilled with surrounding whitespace. Trim here so every
    // client gets the same behaviour instead of each caller trimming (or
    // forgetting to) before calling safeParse.
    token: z.string().trim(),
    isBackupCode: z.boolean().optional(),
})
    .superRefine((data, ctx) => {
    const useBackup = data.isBackupCode === true;
    const formatOk = useBackup ? BACKUP_CODE_RE.test(data.token) : TOTP_TOKEN_RE.test(data.token);
    if (!formatOk) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['token'],
            message: useBackup ? 'Invalid backup code format' : 'Enter your 6-digit code',
        });
    }
});
//# sourceMappingURL=auth.schema.js.map